import axios from 'axios';

import type {
  Address,
  BaseAddress,
  Cart,
  CartStore,
  ProductDeliveryVariant,
  ShippingGroupType,
  UpdateDeliveryInfoCartRequest,
} from '@common/contracts/server/ostin/api';

import { OstinOrderStatusMapper } from '@/api/ostinOrderStatusMapper';
import type { GetBannersResponse } from '@/composables/useBanner/types';
import { CART_STORE_TYPE } from '@/constants/delivery';
import type { ValueOf } from '@/utils/types/value-of';

import type { IStaticPageItem } from './types/static-page';
import { type FetcherAxiosInstance } from './fetcher';
import { menuFormatter, productFormatter } from './formatters';

const createToken = () => {
  const { CancelToken } = axios;
  return CancelToken.source();
};

export type CreateApiReturnType = ReturnType<typeof createApi>;

const createApi = (Fetcher: FetcherAxiosInstance, store: any) => {
  /**
   * маппер для новых статусов, чтобы не периписывать бизнеслогику
   */
  const orderStatusMappper = new OstinOrderStatusMapper();

  /* COMMON */
  /**
   * шортхенд Axios.get для query params
   * @param url
   * @param params Объект с query параметрами
   */
  const get = <T = any>(url: string, params = {}, config = {}) => Fetcher.get(url, { params, ...config }) as Promise<T>;

  /**
   * шортхенд Axios.post для query params
   * @param url
   * @param data Тело запроса
   * @param params Объект с query параметрами
   */
  // TODO: исправить сигнатуру
  // eslint-disable-next-line max-params
  const post = (url: string, data = {}, params = {}, options = {}) => Fetcher({
    url,
    method: 'POST',
    data,
    params,
    ...options,
  });

  // шортхенд для получения города
  const userCity = () => store.getters['user/cityId'];

  return {
    /* MAIN */
    loadMenu: (slots = []) => new Promise((resolve, reject) => {
      get(`/menu/${slots.join()}`, { layout: 'desktop' }) // adaptive возвращает {desktop, mobile}
        .then(menuFormatter)
        .then(resolve)
        .catch(reject);
    }),

    getBanners: (slots: string[], categoryUrl?: string) => get<GetBannersResponse>(`/banner/${slots.join()}`, {
      cityId: userCity(),
      categoryUrl,
    }),

    getStaticPage: (pageUrl: string) => get(`/static-pages/${pageUrl}`),

    getStaticFragment: (slotId = '', cityId = '') => get<IStaticPageItem[]>('/static/static-fragment', {
      cityId: cityId || userCity(),
      slotId,
    }),

    loadRecentlyViewedProducts: () => get('/products/recently-viewed', { cityId: userCity() }),

    loadRecommendationsProducts: (productIds = []) => {
      const tmpProductIds = productIds.map((productId) => `productIds=${productId}`);
      const urlParams = [`cityId=${userCity()}`, ...tmpProductIds]
        .join('&');

      return get(`/recommendations/products?${urlParams}`, {

      });
    },

    /* CATALOG */
    loadCategory: ({
      categoryUrl = '',
      cityId = userCity(),
      pageSize = 96,
      pageNumber = 0,
      sort = '',
      filters = '',
    } = {}) => get(`/category/${categoryUrl}`, {
      cityId,
      pageSize,
      pageNumber,
      sort,
      filters,
    }),

    loadProductPromoKits: ({ productId, cityId = userCity() } = {}) => {
      if (!productId) {
        return Promise.reject(Error('productId is required'));
      }

      return get(`/product/${productId}/promo-kits`, { cityId });
    },

    loadProducts: (ids) => get(`/products/${ids}?cityId=${userCity()}`),

    searchProducts: ({
      query = '',
      pageSize = 96,
      pageNumber = 0,
      sort = '',
      filters = '',
    } = {}) => get('/products', {
      cityId: userCity(),
      query,
      pageSize,
      pageNumber,
      sort,
      filters,
    }),

    getDeliveryTerms: ({ cityId = userCity() } = {}) => get(`/delivery-terms/${cityId}`),

    /* GEO */
    detectCity: () => get('/city'),

    getCities: (ids = [userCity()]) => get(`/cities/${ids}`),

    findCity: ({ query = '', size = 20 } = {}) => get('/cities', { query, size }),

    loadStores: ({ cityId = userCity(), sort } = {}) => get('/stores', { cityId, sort }),

    loadMicroCities: ({ cityId }) => get('/cities/cluster', cityId),

    loadPopularCities: () => get('/cities/popular'),

    loadMetroStations: ({ cityId = userCity() }) => get('/metro-stations', { cityId }),

    findStreet: ({ query = '', cityId = userCity() } = {}) => get('/streets', { query, cityId }),

    findHouses: ({ query = '', streetId } = {}) => get('/houses', { query, streetId }),

    loadAvailabilityList: ({ productId = '', cityId = userCity() } = {}) => get(`/product/${productId}/availability/stores`, { cityId }),

    /* CART/CHECKOUT */
    loadCart: ({ cityId = userCity(), isExpressDelivery = false } = {}) => get('/cart', { cityId, isExpressDelivery }),

    loadHeaderCart: ({ cityId = userCity() } = {}) => get('/cart/products', { cityId }),

    addToCart: ({
      items = [], pvz = {}, storeId, shippingGroupId, cityId = userCity(),
    }) => post('/cart/products/add', {
      items,
      pvz,
      storeId,
      shippingGroupId,
    }, { cityId }),

    cartExist: () => get('/cart/exist'),

    removeFromCart: ({ lineIds = [] }) => post('/cart/products/delete', { lineIds }),

    async setShippingGroup(params: {
      type: ValueOf<typeof ShippingGroupType>;
      lineIds: string[];
      cityId?: string;
      value?: string;
    }): Promise<Cart> {
      const data = {
        type: params.type,
        lineIds: params.lineIds,
        value: params.value,
      };

      const cityId = params.cityId || userCity();

      return post('/cart/shipping-group', data, { cityId });
    },

    async getPickupPoints(props: {
      cityId: string;
      sort: string;
      lineIds?: string[];
      filters?: string;
      cached?: boolean;
    }): Promise<CartStore[]> {
      const data = {
        lineIds: props.lineIds || [],
      };
      const params = {
        cityId: props.cityId,
        sort: props.sort,
        filters: props.filters || `type:${CART_STORE_TYPE.store},${CART_STORE_TYPE.pvz},${CART_STORE_TYPE.postamat},${CART_STORE_TYPE.post}`,
      };
      const options = {
        cached: props.cached || true,
      };

      const result: CartStore[] = await post('/cart/stores', data, params, options);

      return result;
    },

    getStore: (storeId) => get(`/store/${storeId}`),

    getThirdpartyStores: (ids) => get(`/third-party-stores/${ids}`),

    saveDelivery(form: UpdateDeliveryInfoCartRequest): Promise<Cart> {
      return post('/cart/delivery', form, { layout: 'adaptive' });
    },

    getDeliveryVariants(
      props: { cityId?: string; lineIds: string[]; filter?: 'express_delivery' | 'ordinary_delivery' },
    ): Promise<ProductDeliveryVariant[]> {
      const date = new Date();
      const offset = date.getTimezoneOffset(); // Returns the offset in minutes
      const deviceOffset = `${Math.abs(offset / 60)}`; // Convert minutes to hours

      const params = {
        cityId: props.cityId || userCity(),
        lineIds: props.lineIds.join(','),
        deviceOffset, // Сдвиг часового пояса
        filter: props.filter || 'ordinary_delivery',
      };

      return get<ProductDeliveryVariant[]>('/cart/delivery-variants', params);
    },

    setPaymentMethod: (paymentMethod) => post('/cart/payment-method', { paymentMethod }),

    saveContact: ({ contact = {}, token = '' } = {}) => {
      const source = createToken();
      const request = post('/cart/contact', { contact, token }, null, {
        cancelToken: source.token,
      });

      return { request, cancel: source.cancel };
    },

    saveComment(data: { comment: string; shippingGroupId: string }): Promise<Cart> {
      return post('/cart/comment', data);
    },

    setPromo: (promo) => post('/cart/promo/add', { promo }),

    deletePromo: (promo) => post('/cart/promo/delete', { promo }),

    applyPromotions: (apply) => post('/cart/promotions', { apply }),

    applyBonuses: (apply) => post('/cart/bonuses', { apply }),

    checkout: ({
      version = store.getters['cart/version'],
      subscribe = store.state.cart?.subscribe,
      communicationCode = '',
      register,
    } = {}) => post('cart/checkout', {
      version,
      subscribe,
      ...(communicationCode && { communicationCode }),
      register,
    }),

    getShippingChannels: ({ cityId = userCity(), lineIds }: { cityId?: string; lineIds: string[] }) => get('/cart/shipping-channels', { cityId, lineIds: lineIds.join(',') }),

    /* ORDERS */
    getOrders: async ({ pageNumber = 0, pageSize = 20, withReceipts }) => {
      const response = await get('/orders', { pageNumber, pageSize, withReceipts });
      response.orders = response.orders?.map((it) => ({
        ...it,
        currentStatus: orderStatusMappper.mapToEntity({
          shippingGroupType: it.shippingGroupType,
          withDelivery: it.withDelivery,
          status: it.currentStatus,
        }),
      }));
      return response;
    },

    getOrderDetails: async ({ orderNumbers = [], cityId = userCity() }) => {
      const response = await get(`/orders/${orderNumbers.join()}`, { cityId });
      return response.map((it) => ({
        ...it,
        currentStatus: orderStatusMappper.mapToEntity({
          shippingGroupType: it.shippingGroupType,
          withDelivery: it.withDelivery,
          status: it.currentStatus,
        }),
      }));
    },

    getOfflineOrderDetails: async ({ receiptId = '', cityId = userCity() }) => {
      const res = await get(`/orders/receipts/${receiptId}`, { cityId });
      res.status = orderStatusMappper.mapToEntity({
        shippingGroupType: res.shippingGroupType,
        withDelivery: res.withDelivery,
        status: res.status,
      });
      return res;
    },

    getPaymentTools: ({ orderNumber = '', intentionNumber }) => get(`/order/${orderNumber}/payment-tools`, { intentionNumber }),

    getCancelReasons: () => get('/orders/cancel-reasons'),

    cancelOrder: ({ orderNumber = '', form = {} } = {}) => post(`/order/${orderNumber}/cancel`, form),

    extendOrderDate: (orderNumber) => post(`/order/${orderNumber}/extend-storage-date`),

    getPaymentLink: ({
      origin, orderNumber, restOrdersNumbers = [], acquiring,
    } = {}) => {
      if (typeof orderNumber === 'undefined' || orderNumber === null) {
        return Promise.reject(new Error('orderNumber is required'));
      }

      if (typeof origin === 'undefined' || origin === null) {
        return Promise.reject(new Error('origin is required'));
      }

      const urlSuccessSearchParams = new URLSearchParams({
        succeed: orderNumber,
      });
      const urlFailSearchParams = new URLSearchParams({
        failed: orderNumber,
      });

      if (restOrdersNumbers.length) {
        urlSuccessSearchParams.set('rest', restOrdersNumbers);
        urlFailSearchParams.set('rest', restOrdersNumbers);
      }

      const urlSuccess = `${origin}/thankyoupage?${urlSuccessSearchParams.toString()}`;
      const urlFail = `${origin}/thankyoupage?${urlFailSearchParams.toString()}`;

      return get(`/order/${orderNumber}/payment/acquiring`, {
        urlSuccess,
        urlFail,
        acquiring,
      });
    },

    getPaymentFpsQr: async ({ orderNumber = '' } = {}) => {
      const response = await get(`/order/${orderNumber}/payment-qr`);
      return {
        orderNumber,
        ...response,
      };
    },

    getPaymentFpsBanks: () => axios.get('https://qr.nspk.ru/proxyapp/c2bmembers.json').then((r) => r.data),

    acquiringSuccess: ({ orderNumber = store.getters['cart/version'], aqcuiringParameters = '' } = {}) => post(`/order/${orderNumber}/payment/acquiring/success`, { aqcuiringParameters }),

    /* ACCOUNT */
    accountExists: ({ type = 'phone', value, scanUnconfirmed = false }) => {
      if (!value) {
        return Promise.reject('value is required');
      }

      return post('/account/exists', { type, value, scanUnconfirmed });
    },

    verifyCode: ({ phone = '', email, code = '' } = {}) => post('/sms/verification', { phone, email, code }),

    getAccount: () => get('/account'),

    getLevel: () => get('/account/level'),

    updateAccount: ({ form } = {}) => Fetcher.put('/account', form),

    updateSubscription: ({ email } = {}) => post('/subscriptions', { email }),

    changeSubscriptions: (data) => post('/account/subscriptions', data),

    getCoupons: () => get('/account/promo-coupons', { limit: 100 }),

    getPromotions: () => get('/account/promotions'),

    getPublications: (dateRange, pageSize) => get('/publication', {
      cityId: userCity(),
      sort: 'priority',
      dateFrom: dateRange?.from || '',
      dateTo: dateRange?.to || '',
      pageSize,
    }),

    getPublicationPage: (id) => get(`/publication/${id}`),

    loadFavoritesShort: () => get('/favorites/products/ids', undefined),

    loadFavorites: () => new Promise((resolve, reject) => {
      get(
        '/favorites/products',
        { cityId: userCity() },
      )
        .then((favorites) => favorites.map((f) => ({
          ...f,
          models: f.models.map(productFormatter),
        })))
        .then(resolve)
        .catch(reject);
    }),

    addToFavorites: ({ productId, skuId }) => Fetcher({
      url: '/favorites/products',
      method: 'POST',
      params: {
        productId,
        skuId,
        cityId: userCity(),
      },
    }),

    removeFromFavorites: (favoriteId) => Fetcher({
      url: `favorites/products/${favoriteId}`,
      method: 'DELETE',
    }),

    loadBonusHistory: ({ dateFrom, dateTo } = {}) => get('/account/bonus-history', { dateFrom, dateTo }),

    /* ADDRESSES */
    getAddresses: () => get('/account/addresses', undefined),

    createAddress({ address }: { address: BaseAddress }): Promise<void> {
      return post('/account/addresses', address);
    },

    updateAddress({ address }: { address: Address }) {
      return Fetcher.put(`/account/addresses/${address.id}`, address);
    },

    removeAddress: ({ addressId = '' }) => Fetcher.delete(`/account/addresses/${addressId}`),

    // csm
    addCsm: ({ wareId, price, discountPrice }) => Fetcher({
      url: '/csm/add-product-csm',
      method: 'POST',
      data: {
        wareId,
        price,
        discountPrice,
      },
      baseURL: '/',
    }),

    // resend email
    sendConfirmEmail({ email }) {
      return post('/account/reconfirmation/email', {}, {
        email,
      });
    },

    changeEmail(email) {
      return post('/account/email', {
        email,
      }, {}, {
        validateStatus(status) {
          return (status >= 200 && status < 300) || status === 400; // 400 - уже существует в системе
        },
      });
    },

    instance: Fetcher,
  };
};

export default createApi;
